/**
 * Filter drawer — slides in from the left.
 * Opens on `filter_drawer_open` or `[data-filter-drawer-open]` clicks.
 * Facets form refreshes the listing and drawer via the Section Rendering API.
 */
(() => {
  const DRAWER_TAG = 'filter-drawer';
  const FORM_TAG = 'facets-form';
  const OPEN_EVENT = 'filter_drawer_open';
  const PRICE_DEBOUNCE_MS = 400;
  // Link-styled actions the form handles itself: remove pills and both Clear alls
  const ACTION_LINKS =
    '[data-filter-remove], [data-filter-clear-all], .filter-drawer__clear, .filter-drawer__clear-all';
  // Controls that survive a refresh and have no id to find them again by
  const STABLE_CONTROLS = ['.filter-drawer__apply', '.filter-drawer__clear', '.filter-drawer__clear-all'];
  const FOCUSABLE_OPENER =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const isVisible = (element) =>
    element instanceof HTMLElement && element.getClientRects().length > 0;

  const focusableOpener = (element) => {
    if (!(element instanceof HTMLElement) || !element.isConnected) return null;

    if (element.matches(FOCUSABLE_OPENER) && isVisible(element)) return element;

    const nested = element.querySelectorAll(FOCUSABLE_OPENER);

    for (const node of nested) {
      if (isVisible(node)) return node;
    }

    const first = nested[0];
    return first instanceof HTMLElement ? first : null;
  };

  const resolveOpener = (event, marked) => {
    const target = event.target instanceof Element ? event.target : null;
    const clicked = target ? target.closest(FOCUSABLE_OPENER) : null;

    if (clicked instanceof HTMLElement && marked.contains(clicked) && isVisible(clicked)) {
      return clicked;
    }

    return focusableOpener(marked);
  };

  // The visible filter button on the page (the listing re-renders it on refresh).
  // The marker wraps the button and has no box of its own, so check the button.
  const liveOpener = (drawer) => {
    for (const marked of document.querySelectorAll('[data-filter-drawer-open]')) {
      if (drawer?.contains(marked)) continue;

      const opener = focusableOpener(marked);

      if (isVisible(opener)) return opener;
    }

    return null;
  };
  const sectionIdFromListing = () => {
    const listing = document.querySelector('[data-product-listing]');

    if (!(listing instanceof HTMLElement)) return '';

    const fromAttr = listing.getAttribute('data-section-id');

    if (fromAttr) return fromAttr;

    const wrapper = listing.closest('.shopify-section');
    const wrapperId = wrapper?.id || '';

    return wrapperId.replace(/^shopify-section-/, '');
  };

  const parseSectionHtml = (html) => {
    const template = document.createElement('template');

    template.innerHTML = html.trim();

    return template.content;
  };

  class FilterDrawer extends HTMLElement {
    #open = false;
    #previousOverflow = '';
    #opener = null;
    #inerted = [];

    connectedCallback() {
      document.addEventListener(OPEN_EVENT, this.#onOpenEvent);
      document.addEventListener('click', this.#onDocumentClick, true);
      document.addEventListener('keydown', this.#onKeydown);
      this.addEventListener('click', this.#onClick);
      this.#backdrop()?.addEventListener('click', this.#onBackdropClick);
    }

    disconnectedCallback() {
      document.removeEventListener(OPEN_EVENT, this.#onOpenEvent);
      document.removeEventListener('click', this.#onDocumentClick, true);
      document.removeEventListener('keydown', this.#onKeydown);
      this.removeEventListener('click', this.#onClick);
      this.#backdrop()?.removeEventListener('click', this.#onBackdropClick);
      this.#unlockScroll();
      this.#releaseBackground();
    }

    hide() {
      this.#hide();
    }

    #backdrop() {
      return this.querySelector('[data-filter-drawer-backdrop]');
    }

    #panel() {
      return this.querySelector('[data-filter-drawer-panel]');
    }

    #closeButton() {
      return this.querySelector('[data-filter-drawer-close]');
    }

    #onOpenEvent = (event) => {
      const opener = event.detail?.opener;
      this.#opener =
        opener instanceof HTMLElement
          ? focusableOpener(opener) ?? opener
          : document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
      this.#show();
    };

    #onDocumentClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) return;

      const marked = target.closest('[data-filter-drawer-open]');

      if (marked) {
        event.preventDefault();
        this.#opener = resolveOpener(event, marked);
        this.#show();
        return;
      }

      if (!this.#open) return;

      const onBackdrop = Boolean(target.closest('[data-filter-drawer-backdrop]'));
      const panel = this.#panel();
      const outsidePanel = panel instanceof HTMLElement ? !panel.contains(target) : false;

      if (onBackdrop || (outsidePanel && this.contains(target))) {
        event.preventDefault();
        this.#hide();
      }
    };

    #onBackdropClick = (event) => {
      event.preventDefault();
      this.#hide();
    };

    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) return;

      const close = target.closest('[data-filter-drawer-close], .filter-drawer__apply');

      if (close) {
        event.preventDefault();
        this.#hide();
      }
    };

    #onKeydown = (event) => {
      if (event.key === 'Escape' && this.#open) {
        this.#hide();
        return;
      }

      if (event.key !== 'Tab' || !this.#open) return;

      this.#trapTab(event);
    };

    #trapTab(event) {
      const panel = this.#panel();
      if (!panel) return;

      const focusable = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    #show() {
      if (this.#open) return;

      this.#open = true;
      this.setAttribute('data-open', 'true');
      this.setAttribute('aria-hidden', 'false');
      this.#panel()?.setAttribute('aria-modal', 'true');
      this.#lockScroll();
      this.#holdBackground();
      this.#closeButton()?.focus();
    }

    #hide() {
      if (!this.#open) return;

      this.#open = false;
      this.removeAttribute('data-open');
      this.setAttribute('aria-hidden', 'true');
      this.#panel()?.removeAttribute('aria-modal');
      this.#unlockScroll();
      this.#releaseBackground();

      const opener = this.#opener;
      this.#opener = null;

      // Applying filters re-renders the listing, filter bar included, so the
      // button that opened the drawer may be gone, now or by the next frame.
      // Resolve at focus time and fall back to its replacement.
      requestAnimationFrame(() => {
        const original = focusableOpener(opener) ?? opener;
        const focusTarget = original?.isConnected ? original : liveOpener(this);

        focusTarget?.focus();
      });
    }

    #holdBackground() {
      for (const node of document.body.children) {
        if (node === this || node.contains(this)) continue;
        if (node.hasAttribute('inert')) continue;

        node.setAttribute('inert', '');
        this.#inerted.push(node);
      }
    }

    #releaseBackground() {
      for (const node of this.#inerted) {
        node.removeAttribute('inert');
      }

      this.#inerted = [];
    }

    #lockScroll() {
      this.#previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }

    #unlockScroll() {
      document.documentElement.style.overflow = this.#previousOverflow;
      this.#previousOverflow = '';
    }
  }

  class PriceRangeSlider extends HTMLElement {
    #from = null;
    #to = null;
    #fromSubmit = null;
    #toSubmit = null;
    #fromLabel = null;
    #toLabel = null;
    #boundMin = 0;
    #boundMax = 0;
    #divisor = 100;
    #formatter = null;

    connectedCallback() {
      this.#from = this.querySelector('[data-price-thumb="from"]');
      this.#to = this.querySelector('[data-price-thumb="to"]');
      this.#fromSubmit = this.querySelector('[data-price-submit="from"]');
      this.#toSubmit = this.querySelector('[data-price-submit="to"]');
      this.#fromLabel = this.querySelector('[data-price-label="from"]');
      this.#toLabel = this.querySelector('[data-price-label="to"]');
      this.#boundMin = this.#number(this.dataset.boundMin, 0);
      this.#boundMax = this.#number(this.dataset.boundMax, 0);
      this.#divisor = this.#number(this.dataset.divisor, 100) || 1;
      this.#formatter = this.#currencyFormatter();

      this.addEventListener('input', this.#onThumbMove);
      this.addEventListener('change', this.#onThumbMove);
      this.#paint();
    }

    disconnectedCallback() {
      this.removeEventListener('input', this.#onThumbMove);
      this.removeEventListener('change', this.#onThumbMove);
    }

    #number(value, fallback) {
      const parsed = Number(value);

      return Number.isFinite(parsed) ? parsed : fallback;
    }

    #currencyFormatter() {
      const currency = this.dataset.currency || 'GBP';
      const locale = document.documentElement.lang || 'en-GB';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        });
      } catch {
        return null;
      }
    }

    #thumbValue(thumb) {
      if (!(thumb instanceof HTMLInputElement)) return this.#boundMin;

      return this.#number(thumb.value, this.#boundMin);
    }

    #clampPair(source) {
      let from = this.#thumbValue(this.#from);
      let to = this.#thumbValue(this.#to);

      if (from > to) {
        if (source === this.#from) {
          from = to;
        } else {
          to = from;
        }
      }

      from = Math.min(Math.max(from, this.#boundMin), this.#boundMax);
      to = Math.min(Math.max(to, this.#boundMin), this.#boundMax);

      return { from, to };
    }

    #formatLabel(cents) {
      const amount = cents / this.#divisor;

      if (this.#formatter) return this.#formatter.format(amount);

      return this.#divisor === 1 ? String(Math.round(amount)) : amount.toFixed(2);
    }

    #formatSubmit(cents) {
      const amount = cents / this.#divisor;

      if (this.#divisor === 1) return String(Math.round(amount));

      return amount.toFixed(2);
    }

    #writeSubmit(input, cents, atBound) {
      if (!(input instanceof HTMLInputElement)) return false;

      const next = atBound ? '' : this.#formatSubmit(cents);

      if (input.value === next) return false;

      input.value = next;

      return true;
    }

    #emit(type) {
      const input =
        this.#fromSubmit instanceof HTMLInputElement ? this.#fromSubmit : this.#toSubmit;

      input?.dispatchEvent(new Event(type, { bubbles: true }));
    }

    #paint(source) {
      const { from, to } = this.#clampPair(source);
      const span = this.#boundMax - this.#boundMin;
      const fromPct = span > 0 ? ((from - this.#boundMin) / span) * 100 : 0;
      const toPct = span > 0 ? ((to - this.#boundMin) / span) * 100 : 100;

      if (this.#from instanceof HTMLInputElement) this.#from.value = String(from);
      if (this.#to instanceof HTMLInputElement) this.#to.value = String(to);

      this.style.setProperty('--price-from', `${fromPct}%`);
      this.style.setProperty('--price-to', `${toPct}%`);

      if (this.#fromLabel) this.#fromLabel.textContent = this.#formatLabel(from);
      if (this.#toLabel) this.#toLabel.textContent = this.#formatLabel(to);

      const fromChanged = this.#writeSubmit(this.#fromSubmit, from, from <= this.#boundMin);
      const toChanged = this.#writeSubmit(this.#toSubmit, to, to >= this.#boundMax);

      const fromEl = this.#from;
      const toEl = this.#to;

      if (fromEl instanceof HTMLElement && toEl instanceof HTMLElement) {
        const fromOnTop = source === fromEl || (source !== toEl && fromPct > 50);

        fromEl.style.zIndex = fromOnTop ? '2' : '1';
        toEl.style.zIndex = fromOnTop ? '1' : '2';
      }

      return fromChanged || toChanged;
    }

    // Thumb moves only ever emit input: a change commits immediately, which would
    // remount the drawer between keyboard steps and take focus off the thumb.
    #onThumbMove = (event) => {
      const target = event.target;

      if (!(target instanceof HTMLInputElement) || target.type !== 'range') return;

      if (this.#paint(target)) this.#emit('input');
    };
  }

  class FacetsForm extends HTMLElement {
    #abort = null;
    #priceTimer = 0;
    #priceQueued = false;
    #requestToken = 0;
    #roomsToggle = null;
    #spaceAction = null;

    connectedCallback() {
      this.addEventListener('pointerdown', this.#onPointerDown);
      this.addEventListener('keydown', this.#onKeydown);
      this.addEventListener('keyup', this.#onKeyup);
      this.addEventListener('change', this.#onChange);
      this.addEventListener('input', this.#onInput);
      this.addEventListener('click', this.#onClick);
      this.addEventListener('submit', this.#onSubmit);
      window.addEventListener('popstate', this.#onPopState);
    }

    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.#onPointerDown);
      this.removeEventListener('keydown', this.#onKeydown);
      this.removeEventListener('keyup', this.#onKeyup);
      this.removeEventListener('change', this.#onChange);
      this.removeEventListener('input', this.#onInput);
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('submit', this.#onSubmit);
      window.removeEventListener('popstate', this.#onPopState);
      this.#abort?.abort();
      window.clearTimeout(this.#priceTimer);
    }

    #form() {
      return this.querySelector('form');
    }

    #drawerSectionId() {
      return this.closest('filter-drawer')?.getAttribute('data-section-id') || '';
    }

    #onSubmit = (event) => {
      event.preventDefault();
      this.#refreshFromForm();
    };

    #roomsInputFromEvent(event) {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) return null;

      const group = target.closest('[data-filter-rooms]');

      if (!group) return null;

      if (target instanceof HTMLInputElement && target.type === 'radio') return target;

      const label = target.closest('label');
      const input = label?.querySelector('input[type="radio"]');

      return input instanceof HTMLInputElement ? input : null;
    }

    #clearRooms(input) {
      if (!(input instanceof HTMLInputElement) || input.disabled) return false;

      input.checked = false;
      this.#roomsToggle = null;
      this.#refreshFromForm();

      return true;
    }

    #onPointerDown = (event) => {
      const input = this.#roomsInputFromEvent(event);

      this.#roomsToggle = input?.checked ? input : null;
    };

    #onKeydown = (event) => {
      if (event.key !== ' ' && event.key !== 'Spacebar') return;

      // Remove pills and Clear all are links (they work without JS) but act as
      // buttons here, and links only answer Enter. Let Space press them too,
      // the way a button does: keydown only stops the page scrolling, keyup
      // activates. Held-down repeats are ignored, so a refresh that moves focus
      // to the next pill mid-press cannot remove that one as well.
      const action = event.target instanceof Element
        ? event.target.closest(ACTION_LINKS)
        : null;

      if (action instanceof HTMLAnchorElement) {
        event.preventDefault();

        if (!event.repeat) this.#spaceAction = action;

        return;
      }

      const input = this.#roomsInputFromEvent(event);

      if (!input?.checked) return;

      event.preventDefault();
      this.#clearRooms(input);
    };

    #onKeyup = (event) => {
      if (event.key !== ' ' && event.key !== 'Spacebar') return;

      const action = this.#spaceAction;

      this.#spaceAction = null;

      // Only the control the press started on, and only if focus is still on it
      if (!(action instanceof HTMLAnchorElement) || !action.isConnected) return;
      // Escape mid-press closes the drawer before focus moves; a dismissed drawer
      // must not then change the filters.
      if (!this.closest('filter-drawer')?.hasAttribute('data-open')) return;
      if (event.target instanceof Element && event.target.closest(ACTION_LINKS) !== action) return;

      event.preventDefault();
      action.click();
    };

    #onChange = (event) => {
      const target = event.target;

      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
      if (target instanceof HTMLInputElement && target.type === 'range') return;

      if (target instanceof HTMLInputElement && target.hasAttribute('data-filter-price')) {
        this.#cancelPriceRefresh();
        this.#refreshFromForm();
        return;
      }

      this.#refreshFromForm();
    };

    #onInput = (event) => {
      const target = event.target;

      if (!(target instanceof HTMLInputElement) || target.type === 'range') return;
      if (!target.hasAttribute('data-filter-price')) return;

      window.clearTimeout(this.#priceTimer);
      this.#priceQueued = true;
      this.#priceTimer = window.setTimeout(() => {
        this.#priceQueued = false;
        this.#refreshFromForm();
      }, PRICE_DEBOUNCE_MS);
    };

    #cancelPriceRefresh() {
      window.clearTimeout(this.#priceTimer);
      this.#priceQueued = false;
    }

    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) return;

      const roomsInput = this.#roomsInputFromEvent(event);

      if (roomsInput && this.#roomsToggle === roomsInput) {
        event.preventDefault();
        this.#clearRooms(roomsInput);
        return;
      }

      if (target.closest('.filter-drawer__apply')) return;

      const link = target.closest(ACTION_LINKS);

      if (!(link instanceof HTMLAnchorElement)) return;

      event.preventDefault();
      this.#refresh(new URL(link.href, window.location.origin), { push: true });
    };

    #onPopState = () => {
      this.#refresh(new URL(window.location.href), { push: false });
    };

    #urlFromForm() {
      const form = this.#form();

      if (!(form instanceof HTMLFormElement)) return null;

      const url = new URL(form.action, window.location.origin);
      const data = new FormData(form);

      url.search = '';

      for (const [key, value] of data.entries()) {
        if (typeof value !== 'string' || value === '') continue;

        // Facets are multi-valued, sort is not: a hidden input can carry an
        // active sort the radio group has no option for, and the radio that
        // follows it has to win rather than append a second sort_by.
        if (key === 'sort_by') {
          url.searchParams.set(key, value);
          continue;
        }

        url.searchParams.append(key, value);
      }

      url.searchParams.delete('page');
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');

      return url;
    }

    #refreshFromForm() {
      const url = this.#urlFromForm();

      if (!url) return;

      this.#refresh(url, { push: true });
    }

    async #refresh(url, { push }) {
      const listingId = sectionIdFromListing();
      const drawerId = this.#drawerSectionId();
      const sectionIds = [listingId, drawerId].filter(Boolean);

      if (sectionIds.length === 0) return;

      this.#abort?.abort();
      this.#abort = new AbortController();
      const token = ++this.#requestToken;

      const requestUrl = new URL(url.toString());

      requestUrl.searchParams.delete('sections');
      requestUrl.searchParams.delete('section_id');
      requestUrl.searchParams.set('sections', sectionIds.join(','));

      this.setAttribute('aria-busy', 'true');

      const openParams = [];

      for (const details of this.querySelectorAll('details[data-filter-param]')) {
        if (details instanceof HTMLDetailsElement && details.open) {
          openParams.push(details.getAttribute('data-filter-param') || '');
        }
      }

      const scroller = this.querySelector('[data-filter-drawer-scroller]');
      const scrollTop = scroller instanceof HTMLElement ? scroller.scrollTop : 0;

      try {
        const response = await fetch(requestUrl.toString(), {
          signal: this.#abort.signal,
        });

        if (!response.ok || token !== this.#requestToken) return;

        const data = await response.json();

        if (token !== this.#requestToken) return;

        const listingHtml = listingId ? data[listingId] : '';
        const drawerHtml = drawerId ? data[drawerId] : '';
        const listingOk = typeof listingHtml === 'string' && listingHtml !== '';
        const drawerOk = typeof drawerHtml === 'string' && drawerHtml !== '';

        if (!listingOk && !drawerOk) return;

        // A queued price refresh means the inputs moved after this request went
        // out, so both halves of the response describe a superseded range. The
        // queued refresh repaints them; painting now would snap the thumbs back
        // and leave the listing showing the old range.
        if (this.#priceQueued) return;

        if (listingOk) this.#morphListing(listingHtml);

        if (drawerOk) {
          this.#morphDrawer(drawerHtml, openParams, scrollTop);
        }

        if (push) {
          const publicUrl = new URL(url.toString());

          publicUrl.searchParams.delete('sections');
          publicUrl.searchParams.delete('section_id');
          history.pushState({ facets: true }, '', `${publicUrl.pathname}${publicUrl.search}`);
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
      } finally {
        if (token === this.#requestToken) {
          this.removeAttribute('aria-busy');
        }
      }
    }

    #morphListing(html) {
      const listing = document.querySelector('[data-product-listing]');
      const current = listing?.querySelector('.product-listing__inner');
      const next = parseSectionHtml(html).querySelector('.product-listing__inner');

      if (!current || !next) return;

      // A refresh that lands after the drawer closed (See Results pressed while
      // it was in flight) would replace the filter button focus just returned
      // to, dropping focus on the page. Carry it over to the new listing.
      const active = document.activeElement;
      const hadFocus = active instanceof HTMLElement && current.contains(active);
      const onOpener = hadFocus && Boolean(active.closest('[data-filter-drawer-open]'));
      const focusId = hadFocus ? active.id : '';

      current.replaceWith(next);

      if (!hadFocus) return;

      let target = focusId ? next.querySelector(`#${CSS.escape(focusId)}`) : null;

      if (!target && onOpener) target = liveOpener(this.closest('filter-drawer'));

      if (target instanceof HTMLElement) {
        target.focus({ preventScroll: true });
      }
    }

    #morphDrawer(html, openParams, scrollTop) {
      const next = parseSectionHtml(html).querySelector('facets-form');

      if (!next) return;

      // Read focus now, not when the request went out: a keyboard user may have
      // tabbed on while it was in flight, and restoring the older element
      // threw them back to the checkbox they had just left.
      const focusKey = this.#focusKey();

      for (const details of next.querySelectorAll('details[data-filter-param]')) {
        if (!(details instanceof HTMLDetailsElement)) continue;

        const param = details.getAttribute('data-filter-param') || '';

        details.open = openParams.includes(param);
      }

      this.replaceWith(next);

      const scroller = next.querySelector('[data-filter-drawer-scroller]');

      if (scroller instanceof HTMLElement) {
        scroller.scrollTop = scrollTop;
      }

      this.#restoreFocus(next, focusKey);
    }

    /**
     * Describe the focused control in a way that survives the re-render: ids
     * for inputs, the facet for an accordion summary, the position for a
     * remove pill. Null when focus is outside the form, so a refresh never
     * pulls focus back from elsewhere.
     */
    #focusKey() {
      const active = document.activeElement;

      if (!(active instanceof HTMLElement) || !this.contains(active)) return null;
      if (active.id) return { id: active.id };

      const details = active.closest('details[data-filter-param]');

      if (active.matches('summary') && details) {
        return { summary: details.getAttribute('data-filter-param') || '' };
      }

      const pillIndex = [...this.querySelectorAll('[data-filter-remove]')].indexOf(active);

      if (pillIndex >= 0) return { pill: pillIndex };

      const stable = STABLE_CONTROLS.find((selector) => active.matches(selector));

      if (stable) return { selector: stable };

      return {};
    }

    #restoreFocus(next, key) {
      if (!key) return;

      let target = null;

      if (key.id) {
        target = next.querySelector(`#${CSS.escape(key.id)}`);
      } else if (key.summary != null) {
        target = next.querySelector(
          `details[data-filter-param="${CSS.escape(key.summary)}"] > summary`
        );
      } else if (key.pill != null) {
        const pills = next.querySelectorAll('[data-filter-remove]');

        target = pills[Math.min(key.pill, pills.length - 1)] || null;
      } else if (key.selector) {
        target = next.querySelector(key.selector);
      }

      // A control that is now disabled hands focus to its facet heading
      if (target instanceof HTMLInputElement && target.disabled) {
        target = target.closest('details')?.querySelector(':scope > summary') || null;
      }

      // A removed pill or cleared filters take their control with them, so stay
      // inside the drawer on the first facet heading that is actually shown
      // (Sort is hidden from 750px) instead of losing focus to the page.
      if (!(target instanceof HTMLElement) || !isVisible(target)) {
        target = [...next.querySelectorAll('details[data-filter-param] > summary')].find(isVisible) || null;
      }

      if (target instanceof HTMLElement) {
        target.focus({ preventScroll: true });
      }
    }
  }

  if (!customElements.get(DRAWER_TAG)) {
    customElements.define(DRAWER_TAG, FilterDrawer);
  }

  if (!customElements.get(FORM_TAG)) {
    customElements.define(FORM_TAG, FacetsForm);
  }

  if (!customElements.get('price-range-slider')) {
    customElements.define('price-range-slider', PriceRangeSlider);
  }
})();
